import { ResponseMESF, useUTLSettingsContext } from "@dexteel/mesf-core";
import { Box, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useState } from "react";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { CenteredLazyLoading } from "../../../../../../controls/LazyLoading";
import { dataEntryTableStyles } from "../../../../../../shared/styles/dataEntryTableStyles";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useTimerContext } from "../../../context/timer.provider";
import { useCasterDataEntryActions } from "../../../hooks/useCasterDataEntryActions";
import { useJustifyAllStrandLosses } from "../../../hooks/useJustifyAllStrandLosses";
import { useStrandLossJustify } from "../../../hooks/useStrandLossJustify";
import type { Strand } from "../../../models/caster.models";
import { readStrandFromPLC } from "../../../repositories/caster.repositories";
import { EditStrand } from "../../modals/strand/EditStrand";
import { useTableData } from "./hooks/useStrandsTableData";

export const TableStrands = () => {
  const theme = useTheme();
  const styles = dataEntryTableStyles(theme);
  const { pause, resume } = useTimerContext();
  const { saveStrand, refreshData } = useCasterDataEntryActions();
  const {
    state: { deProcess },
    actions: { setNotificationMessage, setErrorMessage },
  } = useDataEntryContext();
  const strands = deProcess?.strands ?? [];
  const [selectedStrand, setSelectedStrand] = useState<Strand | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const { openStrandLoss } = useStrandLossJustify();
  const { canJustifyAll, isJustifyingAll, openJustifyAll } =
    useJustifyAllStrandLosses();

  const { state } = useUTLSettingsContext();
  const minSHeatTemp = get(state.settings, "CAS_DE_SUPER_HEAT_MIN_TEMP", 30);
  const readAutomaticStrands = async (
    value?: string,
    strandNumber?: number,
  ) => {
    if (value === "OK" && strandNumber !== undefined) {
      const resp = await readStrandFromPLC({
        ProcessId: deProcess!.process!.ProcessId,
        Strand: strandNumber,
      });
      if (resp.ok) {
        setNotificationMessage("Read strand from PLC successful");
        await refreshData();
        return resp as ResponseMESF;
      } else {
        setErrorMessage(resp.message);
        return {
          ok: false,
          message: "Read strand from PLC failed",
        } as ResponseMESF;
      }
    } else {
      return {} as ResponseMESF;
    }
  };

  const editStrand = (Strand: number) => {
    pause();
    const strand = strands.find((p) => p.Strand === Strand);
    setSelectedStrand(strand ?? null);
    setShowEditModal(true);
  };

  const editChecbox = (
    checked: boolean,
    strand: Strand,
    attributeName: keyof Strand,
  ) => {
    saveStrand({
      ...strand,
      [attributeName]: checked,
    });
  };

  const { columns } = useTableData({
    editCheckbox: editChecbox,
    editStrand,
    readAutomaticStrands,
    openLossDetails: openStrandLoss,
    canJustifyAllLosses: canJustifyAll,
    isJustifyingAllLosses: isJustifyingAll,
    justifyAllLosses: openJustifyAll,
  });

  return (
    <Paper>
      <Box padding={1} sx={styles.agGridBox}>
        <AgGridReact
          rowData={strands}
          columnDefs={columns}
          defaultColDef={{ ...defaultColDef, sortable: false }}
          loadingOverlayComponent={CenteredLazyLoading}
          {...defaultGridOptions}
        />
        <Box
          padding={1}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
        >
          <span
            style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#1f77b4" }}
          >
            Liquidus Temp: {deProcess?.process?.LiquidusTemp ?? "N/A"} °F
          </span>
          <span
            style={{
              fontSize: "0.9rem",
              fontWeight: "bold",
              color: "#1f77b4",
              paddingLeft: "1.5rem",
            }}
          >
            Tundish Temp: {deProcess?.process?.TundishTemp ?? "N/A"} °F
          </span>
          <span
            style={{
              fontSize: "0.9rem",
              fontWeight: "bold",
              color:
                deProcess?.process?.SuperHeat !== undefined &&
                (deProcess?.process?.SuperHeat ?? 0) < minSHeatTemp
                  ? "#d62728"
                  : "#1f77b4",
              paddingLeft: "1.5rem",
            }}
          >
            Super Heat: {deProcess?.process?.SuperHeat ?? "N/A"} °F
          </span>
        </Box>
      </Box>
      {selectedStrand && (
        <EditStrand
          show={showEditModal}
          onHide={() => {
            resume();
            setShowEditModal(false);
          }}
          strand={selectedStrand}
        />
      )}
    </Paper>
  );
};
